import {
  Address,
  Country,
  Flex,
  InternationalPhoneData,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import DeviceData from './DeviceData';
import { useEffect, useRef, useState } from 'react';
import DeviceLocationConfirmation from './DeviceLocationConfirmation';
import DeviceRetrieval from './DeviceRetrieval';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { Employee } from '@/types/employees';
import DeviceShipping from './DeviceRetrieval/DeviceShipping';
import { getDeviceShippingSchema } from './DeviceRetrieval/DeviceRetrievalSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { DeviceDataAction, DeviceRetrieveAction } from './types';
import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { SiteOption } from '../Assets/helpers/transformOptions';
import { Asset } from '@/types/assets';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';

type DeviceOffboardingFormData = {
  deviceRetrieveAction: DeviceRetrieveAction | null;
  deviceDataAction: DeviceDataAction | null;
  senderAddress?: Address | null;
  senderPhone?: InternationalPhoneData;
  receiverSite?: SiteOption | null;
  receiverPhone?: InternationalPhoneData;
};

type DevicesProps = {
  assets: Asset[];
  employee: Employee;
  onAssetSerialNumberUpdated: (assetId: string, serialNumber: string) => void;
};

enum DevicesSteps {
  LocationConfirmation = 0,
  Retrieval = 1,
  Data = 2,
  Shipping = 3,
}

const Devices = ({
  assets,
  employee,
  onAssetSerialNumberUpdated,
}: DevicesProps) => {
  const { t } = useTranslation('common');
  const needsLocationConfirmation = !employee?.address?.country;
  const [currentStep, setCurrentStep] = useState(
    needsLocationConfirmation
      ? DevicesSteps.LocationConfirmation
      : DevicesSteps.Retrieval
  );
  const [currentDevice, setCurrentDevice] = useState(0);
  // Seeded from the employee's address (if known); also set when the admin
  // picks a country on DeviceLocationConfirmation (address not on file).
  const [employeeCountry, setEmployeeCountry] = useState<Country | undefined>(
    employee?.address?.country as Country | undefined
  );
  const isInternationalFlow =
    !!employeeCountry && employeeCountry.toUpperCase() !== 'US';
  const isEmployeeCaOrPr =
    !!employeeCountry && ['CA', 'PR'].includes(employeeCountry.toUpperCase());

  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const employeeId = queryParams.get(SelectionParamKeys.EmployeeId);

  const organizationHasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const methods = useForm<DeviceOffboardingFormData>({
    resolver: yupResolver(getDeviceShippingSchema(t, isInternationalFlow)),
    mode: 'onTouched',
    defaultValues: {
      deviceRetrieveAction: null,
      deviceDataAction: null,
      senderAddress: {
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        state: '',
        country: '',
        zip: '',
      },
      senderPhone: undefined,
      receiverSite: null,
      receiverPhone: undefined,
    },
  });

  // isInternationalFlow can flip after the form is already mounted (going
  // back and picking a different country), so re-validate against the updated
  // schema right away instead of waiting for the next field interaction. Skip
  // the initial mount so we don't trigger validation on an untouched form
  // (mode: 'onTouched' relies on that).
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternationalFlow]);

  const deviceRetrieveAction = methods.watch('deviceRetrieveAction');
  const shouldShowShippingStep =
    deviceRetrieveAction === DeviceRetrieveAction.Ship ||
    deviceRetrieveAction === DeviceRetrieveAction.Warehouse;

  // The Data step only has meaningful options (wipe/lock) when the asset
  // has an MDM-enrolled device and the org has the MDM entitlement.
  // Skip it entirely when only "Keep" would be available, or when the asset
  // has no serial number: DeviceData throws while rendering without one (this
  // tree has no error boundary), and the lock command requires it.
  const shouldSkipDataStep = (asset: Asset) =>
    !asset.device?.mdmEnrolledAt ||
    !organizationHasMdmEntitlement ||
    !asset.serialNumber;

  const handlePrevious = () => {
    const hasComeByNavigation = history.state.idx > 0;

    if (currentStep === DevicesSteps.Data) {
      // The Retrieval step is always visited before Data (regardless of
      // whether the employee's address was on file), so Previous always
      // goes back to Retrieval for the same device.
      setCurrentStep(DevicesSteps.Retrieval);
      return;
    }

    if (currentStep === DevicesSteps.Retrieval) {
      if (needsLocationConfirmation) {
        // If the user is at the Retrieval step and there is no address
        // go back to the Location Confirmation step
        setCurrentStep(DevicesSteps.LocationConfirmation);
        return;
      }
    }

    if (currentStep === DevicesSteps.Shipping) {
      setCurrentStep(
        shouldSkipDataStep(assets[currentDevice])
          ? DevicesSteps.Retrieval
          : DevicesSteps.Data
      );
      return;
    }

    // If the user is on the Data step of the first device on the list and has navigated to the page,
    // go back to the previous page, otherwise go to the employee's profile page
    if (hasComeByNavigation) {
      navigate(-1);
      return;
    }

    navigate(`/employees/${employeeId}`);
  };

  const handleNext = () => {
    const isLastDevice = currentDevice === assets.length - 1;

    // If the user is at the Retrieval step, move to the Data step
    // or skip it if only "Keep" would be available.
    if (currentStep === DevicesSteps.Retrieval) {
      if (shouldSkipDataStep(assets[currentDevice])) {
        methods.setValue('deviceDataAction', DeviceDataAction.Keep);
        if (shouldShowShippingStep) {
          setCurrentStep(DevicesSteps.Shipping);
        } else if (isLastDevice) {
          navigate(`/employees/${employeeId}`);
        } else {
          methods.reset();
          setCurrentDevice(prev => prev + 1);
          setCurrentStep(DevicesSteps.Retrieval);
        }
      } else {
        setCurrentStep(DevicesSteps.Data);
      }
      return;
    }

    // We only show the Shipping step if one of the shipping options was selected
    if (currentStep === DevicesSteps.Data && shouldShowShippingStep) {
      setCurrentStep(DevicesSteps.Shipping);
      return;
    }

    // If the user is at the Data step and the current device is the last one,
    // navigate to the employee's profile page
    if (isLastDevice) {
      navigate(`/employees/${employeeId}`);
      return;
    }

    // Otherwise, reset the form and move to the Retrieval step for the next device
    methods.reset();
    setCurrentDevice(prev => prev + 1);
    setCurrentStep(DevicesSteps.Retrieval);
  };

  return (
    <Flex width="100%">
      <FormProvider {...methods}>
        {currentStep === DevicesSteps.LocationConfirmation && (
          <DeviceLocationConfirmation
            defaultCountry={employeeCountry}
            onNext={(country: Country) => {
              // A site selected under the previous country may not be valid
              // for the new one (US flow requires US sites, international
              // ship-to-site requires CA/PR), so make the admin re-select.
              if (country !== employeeCountry) {
                // shouldValidate keeps isValid in sync with the cleared value.
                methods.setValue('receiverSite', null, {
                  shouldValidate: true,
                });
              }
              setEmployeeCountry(country);
              setCurrentStep(DevicesSteps.Retrieval);
            }}
          />
        )}
        {currentStep === DevicesSteps.Retrieval && (
          <DeviceRetrieval
            asset={assets[currentDevice]}
            onNext={handleNext}
            onPrevious={handlePrevious}
            shouldDisablePrevious={currentDevice !== 0}
            isInternationalFlow={isInternationalFlow}
            isEmployeeCaOrPr={isEmployeeCaOrPr}
            onAssetSerialNumberUpdated={onAssetSerialNumberUpdated}
          />
        )}
        {currentStep === DevicesSteps.Data && (
          <DeviceData
            asset={assets[currentDevice]}
            employee={employee}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}
        {currentStep === DevicesSteps.Shipping && (
          <DeviceShipping
            asset={assets[currentDevice]}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isInternationalFlow={isInternationalFlow}
            employeeCountry={employeeCountry}
          />
        )}
      </FormProvider>
    </Flex>
  );
};

export default Devices;
